import { connectionDisplayTitle, type SavedConnection } from '../../../api/connectionModel';
import type { GoogleBackupStatus, RestoreResult } from '../../../data/backup';
import type { NotifyPermission } from '../../../lib/notify';
import type { ThemePreference } from '../../../settings';
import { normalizePlaybackSpeed } from '../../format';

// Pure helpers behind the settings screens (copy and rules from spec 10 §3-§11
// plus the PWA-only rows). Everything here is unit tested.

/** `AppBuildInfo.versionLabel` (app_settings.dart:14-24). */
export function versionLabel(version: string, buildNumber = ''): string {
  const v = version.trim();
  const b = buildNumber.trim();
  if (!v && !b) return 'Version unavailable';
  if (!b) return `v${v}`;
  return `v${v} (${b})`;
}

/** About hero pill: app name and version separated by two spaces ("Plume  v1.0.0"). */
export function aboutPillLabel(appName: string, label: string): string {
  return `${appName.trim() || 'Plume'}  ${label}`;
}

/**
 * General › Customize Home subtitle (general_settings_screen.dart:166-173).
 * Polish pass: counts the sections the sheet lists and how many are shown
 * (Flutter counted the server's shelves only, "4 home sections are ready to
 * curate." over a sheet of six).
 */
export function customizeHomeSubtitle(sectionCount: number, shownCount: number = sectionCount): string {
  if (sectionCount <= 0) return 'Show or hide Home sections when your library is loaded.';
  if (shownCount >= sectionCount) return `All ${sectionCount} sections shown.`;
  if (shownCount <= 0) return `All ${sectionCount} sections hidden.`;
  return `${shownCount} of ${sectionCount} sections shown.`;
}

/** Default speed ± button: one 0.05 step on Flutter's grid. */
export function stepSpeed(speed: number, delta: number): number {
  return normalizePlaybackSpeed(speed + delta);
}

/**
 * "Add sleep timer preset" field: Dart `int.tryParse(text.trim())`; null,
 * zero or negative values are ignored (playback_settings_screen.dart:73-111).
 */
export function parsePresetMinutes(text: string): number | null {
  const t = text.trim();
  if (!/^[+-]?\d+$/.test(t)) return null;
  const n = Number.parseInt(t, 10);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Skip-interval choices (PWA strength kept from the original settings screen). */
export const SKIP_INTERVAL_OPTIONS = [5, 10, 15, 30, 45, 60] as const;

/** "10s". */
export function formatSkipSeconds(seconds: number): string {
  return `${seconds}s`;
}

/** Options for the skip menus; a stored value outside the list (from a backup) stays selectable. */
export function skipIntervalOptions(current: number): number[] {
  const list: number[] = [...SKIP_INTERVAL_OPTIONS];
  if (!list.includes(current)) list.push(current);
  return list.sort((a, b) => a - b);
}

export interface PermissionCopy {
  /** Whether the row offers the "Allow" action. */
  canRequest: boolean;
  title: string;
  subtitle: string;
}

/** The permission row of the Notifications section; null once notifications are allowed. */
export function notificationPermissionCopy(permission: NotifyPermission): PermissionCopy | null {
  switch (permission) {
    case 'granted':
      return null;
    case 'default':
      return { canRequest: true, title: 'Allow notifications', subtitle: 'Get told when downloads finish or the sleep timer stops playback.' };
    case 'denied':
      return { canRequest: false, title: 'Notifications blocked', subtitle: 'Turn them on for this site in your browser or device settings.' };
    case 'needs-install':
      return {
        canRequest: false,
        title: 'Add Plume to your Home Screen',
        subtitle: 'On iPhone and iPad, notifications work once Plume is added to your Home Screen (iOS 16.4+).',
      };
    default:
      return { canRequest: false, title: 'Notifications unavailable', subtitle: 'This browser does not support notifications.' };
  }
}

/** Notification switches can only do something while permission is granted or still askable. */
export function notificationSwitchesEnabled(permission: NotifyPermission): boolean {
  return permission === 'granted' || permission === 'default';
}

export type InstallMode = 'installed' | 'justInstalled' | 'prompt' | 'ios' | 'manual';

/**
 * Which install row the Settings home shows: none when already running as an
 * installed app; "just installed" when the browser reported an install in
 * this tab (which is still a browser tab, so it must not ask to install again).
 */
export function installMode(opts: { standalone: boolean; installedThisSession?: boolean; canInstall: boolean; isIOS: boolean }): InstallMode {
  if (opts.standalone) return 'installed';
  if (opts.installedThisSession) return 'justInstalled';
  if (opts.canInstall) return 'prompt';
  return opts.isIOS ? 'ios' : 'manual';
}

/** Settings home › Appearance pill: the chosen theme, as the Appearance page names it. */
export function themeLabel(theme: ThemePreference): string {
  if (theme === 'dark') return 'Dark';
  if (theme === 'system') return 'System';
  return 'Light';
}

export const CONNECTIONS_ROW_FALLBACK = 'Manage servers, libraries, and active listening sources.';

/**
 * General › Connections subtitle: the active connection as its list row names
 * it (nickname or server) and the account, "Home Library · reader". Without
 * one, Flutter's description.
 */
export function connectionsRowSubtitle(active: SavedConnection | null): string {
  if (!active) return CONNECTIONS_ROW_FALLBACK;
  return [connectionDisplayTitle(active), active.username?.trim() || null].filter(Boolean).join(' · ');
}

/** Account row subtitle: server address and ABS version. */
export function accountSubtitle(serverUrl: string, serverVersion?: string): string {
  const version = serverVersion?.trim();
  return version ? `${serverUrl} · ABS ${version}` : serverUrl;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Toast after a backup was restored (file or Google Drive). */
export function restoreSummary(result: RestoreResult): string {
  const parts: string[] = [];
  if (result.settings > 0) parts.push('settings');
  if (result.readingLists > 0) parts.push(plural(result.readingLists, 'reading list'));
  if (result.homeSectionScopes > 0) parts.push('Home sections');
  if (result.weeklyGoals > 0) parts.push(plural(result.weeklyGoals, 'weekly goal'));
  if (result.historyEvents > 0) parts.push(plural(result.historyEvents, 'listening event'));
  if (parts.length === 0) return 'Backup restored. It had nothing new to apply.';
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `Restored ${list}.`;
}

/** Saved servers in a backup that this device does not have yet (they need signing in again). */
export function missingBackupConnections(result: Pick<RestoreResult, 'connections'>, existing: ReadonlyArray<{ id: string; url: string; userId: string }>): number {
  return result.connections.filter((c) => !existing.some((e) => e.id === c.id || (e.url === c.url && e.userId === c.userId))).length;
}

/** A Google Drive backup, restore or delete is running (one at a time). */
export function isDriveBusy(status: GoogleBackupStatus): boolean {
  return status === 'backingUp' || status === 'restoring' || status === 'deleting';
}

/** A CSP violation report for Google's sign-in script (accounts.google.com). */
export function isGisBlockedReport(event: Pick<SecurityPolicyViolationEvent, 'blockedURI'>): boolean {
  return typeof event.blockedURI === 'string' && event.blockedURI.startsWith('https://accounts.google.com');
}

/** Radio-group keyboard pattern: the option an arrow / Home / End key moves to (wrapping), or null for other keys. */
export function radioKeyTarget(key: string, index: number, count: number): number | null {
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
      return (index + 1) % count;
    case 'ArrowUp':
    case 'ArrowLeft':
      return (index - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}
